<template>
  <div class="v2-collection-page">
    <!-- Loading -->
    <div
      v-if="!isLoaded"
      class="v2-page-loading"
    >
      <v2-spinner
        size="lg"
        label="Loading..."
      />
    </div>

    <template v-else>
      <!-- Header -->
      <div class="v2-flex v2-justify-between v2-items-center v2-mb-4">
        <h1 class="v2-heading-sm">
          <!--
            KP-3220: Heading was reading currentAssociationEntityName first,
            which resolves via a userAssociations.find() lookup keyed on
            lastLoggedUserAssociationId — that key can lag the actual brand
            switch when PLG triggers IFRAME_TOKEN_EXCHANGE, leaving the
            previous brand's name on the page after a switch.
            profile.entityName comes from the entityData fetched with the
            *current* currentEntityId in FETCH_PROFILE, so it always
            matches the brand the iframe is authenticated for.
          -->
          {{ (profile && profile.entityName) || currentAssociationEntityName }}'s Collections
        </h1>
        <v2-button
          v-if="isBrand && isEntityAdmin"
          @click="onAddCollection"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line
              x1="12"
              y1="5"
              x2="12"
              y2="19"
            />
            <line
              x1="5"
              y1="12"
              x2="19"
              y2="12"
            />
          </svg>
          Add Collection
        </v2-button>
      </div>

      <!-- Live Collections -->
      <template v-if="brandCollection.length">
        <h2 class="v2-collection-page__section-title v2-mb-3">
          Live Collections
        </h2>
        <draggable
          tag="div"
          class="v2-collection-page__grid"
          :list="brandCollection"
          :sort="isBrand"
          @change="onDragged"
        >
          <v2-collection-card
            v-for="collection in brandCollection"
            :key="collection._id"
            :collection="collection"
            :fetch-collections="fetchCollections"
            @show-delete-modal="openDeleteModal"
            @show-download-modal="openDownloadModal"
            @show-modal="onShowUpdateAvailability"
            @show-price-books="onShowPriceBooks"
            @show-currency-books="onShowCurrencyBooks"
            @show-segmentation-books="onShowSegmentationBooks"
          />
        </draggable>
      </template>

      <!-- Previous Collections -->
      <template v-if="previousCollection.length">
        <h2 class="v2-collection-page__section-title v2-mb-3 v2-mt-6">
          Previous Collections
        </h2>
        <div class="v2-collection-page__grid">
          <v2-collection-card
            v-for="collection in previousCollection"
            :key="collection._id"
            :collection="collection"
            is-previous-collection
            :fetch-collections="fetchCollections"
            @show-delete-modal="openDeleteModal"
            @show-download-modal="openDownloadModal"
            @show-modal="onShowUpdateAvailability"
            @show-price-books="onShowPriceBooks"
            @show-currency-books="onShowCurrencyBooks"
            @show-segmentation-books="onShowSegmentationBooks"
          />
        </div>
      </template>

      <!-- Empty State -->
      <div
        v-if="!brandCollection.length && !previousCollection.length"
        class="v2-collection-page__empty"
      >
        <p class="v2-text-muted v2-mb-4">
          No Collections Found
        </p>
        <p class="v2-text-muted v2-mb-4">
          You don't have any uploaded collections
        </p>
        <v2-button
          v-if="isBrand && isEntityAdmin"
          @click="onAddCollection"
        >
          Add Collection
        </v2-button>
      </div>

      <!-- Delete Collection Modal -->
      <v2-delete-collection-modal
        :show="showDeleteModal"
        :message="collectionRemoveMessage"
        :can-delete="canDeleteCollection"
        :is-saving="isSaving"
        @close="showDeleteModal = false"
        @confirm="onConfirmDelete"
      />

      <!-- Download Catalog Modal -->
      <v2-download-catalog-modal
        :show="showDownloadModal"
        :collection="selectedCollection"
        :retailer-id="downloadRetailerId"
        @close="showDownloadModal = false"
      />

      <!-- Update Availability Modal -->
      <v2-update-availability-modal
        :show="showUpdateAvailabilityModal"
        :collection="selectedCollection"
        :fetch-collections="fetchCollections"
        @close="showUpdateAvailabilityModal = false"
      />

      <!-- Price Books Modal -->
      <v2-price-books-modal
        :show="showPriceBooksModal"
        :collection="selectedCollection"
        :fetch-collections="fetchCollections"
        @close="showPriceBooksModal = false"
      />

      <!-- Currency Books Modal -->
      <v2-currency-books-modal
        ref="currencyBooksModal"
        :show="showCurrencyBooksModal"
        :collection="selectedCollection"
        :fetch-collections="fetchCollections"
        @show-currency-update-warning="onShowCurrencyUpdateWarning"
        @close="showCurrencyBooksModal = false"
      />

      <!-- Segmentation Books Modal -->
      <v2-segmentation-books-modal
        :show="showSegmentationBooksModal"
        :collection="selectedCollection"
        :fetch-collections="fetchCollections"
        @close="showSegmentationBooksModal = false"
      />

      <!-- Currency Update Warning Modal -->
      <v2-currency-update-modals
        :show="showCurrencyUpdateModal"
        :collection-id="selectedCollection._id"
        :order-data="currencyUpdateOrderData"
        @do-not-update-orders="onCurrencyDoNotUpdate"
        @update-orders="onCurrencyUpdateOrders"
        @close="showCurrencyUpdateModal = false"
      />
    </template>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Button from '@/components/v2/V2Button.vue'
import { useCollectionList } from '@/composables/useCollectionList'
import { useCollectionCard } from '@/composables/useCollectionCard'
import V2CollectionCard from './V2CollectionCard.vue'
import V2DeleteCollectionModal from './V2DeleteCollectionModal.vue'
import V2DownloadCatalogModal from './V2DownloadCatalogModal.vue'
import V2UpdateAvailabilityModal from './V2UpdateAvailabilityModal.vue'
import V2PriceBooksModal from './V2PriceBooksModal.vue'
import V2CurrencyBooksModal from './V2CurrencyBooksModal.vue'
import V2SegmentationBooksModal from './V2SegmentationBooksModal.vue'
import V2CurrencyUpdateModals from './V2CurrencyUpdateModals.vue'
import UserRoleMixin from '../../apps/UserRoleMixin.vue'

export default {
  name: 'V2CollectionList',
  components: {
    draggable,
    V2CollectionCard,
    V2DeleteCollectionModal,
    V2DownloadCatalogModal,
    V2UpdateAvailabilityModal,
    V2PriceBooksModal,
    V2CurrencyBooksModal,
    V2SegmentationBooksModal,
    V2CurrencyUpdateModals,
    V2Spinner,
    V2Button,
  },
  mixins: [UserRoleMixin],
  setup() {
    const composable = useCollectionList()
    composable.fetchCollections()
    const {
      checkRemoveCollection,
      removeCollection,
      canDeleteCollection,
      isSaving,
      collectionRemoveMessage,
    } = useCollectionCard({})
    return {
      ...composable,
      checkRemoveCollection,
      removeCollection,
      canDeleteCollection,
      isSaving,
      collectionRemoveMessage,
    }
  },
  data() {
    return {
      selectedCollection: {},
      showDeleteModal: false,
      showDownloadModal: false,
      showUpdateAvailabilityModal: false,
      showPriceBooksModal: false,
      showCurrencyBooksModal: false,
      showSegmentationBooksModal: false,
      showCurrencyUpdateModal: false,
      currencyUpdateOrderData: [],
    }
  },
  computed: {
    downloadRetailerId() {
      return this.$store.getters.selectedDirectRetailer?.retailerId
        || this.$route.query.retailerId
        || null
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.$store.state.collection.isUploadingCurrencyBook) {
      // eslint-disable-next-line no-alert
      const answer = window.confirm('Do you really want to leave? Some changes may get lost.')
      if (answer) {
        next()
      } else {
        next(false)
      }
    } else {
      next()
    }
  },
  methods: {
    onAddCollection() {
      this.addCollection()
      this.$router.push({ name: 'v2-collection/edit' })
    },
    openDeleteModal(collection) {
      if (!collection || !collection._id) return
      this.selectedCollection = collection
      this.checkRemoveCollection(collection._id, {
        onSuccess: () => {
          this.fetchCollections()
        },
        onNeedsConfirm: () => {
          this.showDeleteModal = true
        },
      })
    },
    onConfirmDelete() {
      this.removeCollection(this.fetchCollections, {
        onSuccess: () => {
          this.showDeleteModal = false
        },
      })
    },
    openDownloadModal(collectionId) {
      const collection = [...this.brandCollection, ...this.previousCollection]
        .find(c => c._id === collectionId)
      if (collection) {
        this.selectedCollection = collection
        this.showDownloadModal = true
      }
    },
    onShowUpdateAvailability(collection) {
      this.selectedCollection = collection
      this.showUpdateAvailabilityModal = true
    },
    onShowPriceBooks(collection) {
      this.selectedCollection = collection
      this.showPriceBooksModal = true
    },
    onShowCurrencyBooks(collection) {
      this.selectedCollection = collection
      this.showCurrencyBooksModal = true
    },
    onShowSegmentationBooks(collection) {
      this.selectedCollection = collection
      this.showSegmentationBooksModal = true
    },
    onShowCurrencyUpdateWarning(payload) {
      this.currencyUpdateOrderData = payload.orderData
      this.showCurrencyUpdateModal = true
    },
    async onCurrencyDoNotUpdate() {
      try {
        await this.$refs.currencyBooksModal.importCurrencyBookFile([])
      } finally {
        this.showCurrencyUpdateModal = false
        this.fetchCollections()
      }
    },
    async onCurrencyUpdateOrders(orderIds) {
      try {
        await this.$refs.currencyBooksModal.importCurrencyBookFile(orderIds)
      } finally {
        this.showCurrencyUpdateModal = false
        this.fetchCollections()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-collection-page {
  &__section-title {
    font-family: var(--v2-font-heading);
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--v2-foreground);
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, 290px);
    gap: var(--v2-space-4);
  }

  &__empty {
    text-align: center;
    padding: var(--v2-space-12) var(--v2-space-4);
  }
}

.v2-text-muted {
  color: var(--v2-muted-foreground);
}
</style>
